// Reunderwriting Module (Tier 3) PR 4 — executive Escalations surface
// scoped to reunderwriting cases. Mirrors the dealflow EscalationsPage:
// default filter Status=Pending so action items surface first.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpCircle, Download } from "lucide-react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useReunderwritingV1 } from "@/hooks/reunderwriting/useFeatureFlag";
import {
  useApproveReunderwritingEscalation,
  useDenyReunderwritingEscalation,
  useReunderwritingEscalation,
  useReunderwritingEscalations,
} from "@/hooks/reunderwriting/useReunderwriting";
import { reunderwritingEscalationsApi } from "@/api/reunderwriting";
import {
  REUNDERWRITING_PERMISSIONS,
  type CriteriaLane,
  type ReunderwritingEscalationFilter,
  type ReunderwritingEscalationStatus,
} from "@/types/reunderwriting";
import { ReunderwritingEscalationFilterSidebar } from "./escalations/EscalationFilterSidebar";
import { ReunderwritingEscalationList } from "./escalations/EscalationList";
import { ReunderwritingEscalationDetailDrawer } from "./escalations/EscalationDetailDrawer";

const STATUSES: ReunderwritingEscalationStatus[] = ["Pending", "Approved", "Denied"];
const LANES: CriteriaLane[] = ["A1", "A2", "A3", "A4"];

function parseStatus(v: string | null): ReunderwritingEscalationStatus | undefined {
  return STATUSES.find((s) => s === v);
}
function parseLane(v: string | null): CriteriaLane | undefined {
  return LANES.find((l) => l === v);
}

export function ReunderwritingEscalationsPage() {
  const flagOn = useReunderwritingV1();
  const { hasPermission } = useAuth();
  const canResolve = hasPermission(REUNDERWRITING_PERMISSIONS.ResolveEscalation);

  const [params, setParams] = useSearchParams();

  // Default to Pending on first load so executives see action items first.
  useEffect(() => {
    if (!params.get("status")) {
      const next = new URLSearchParams(params);
      next.set("status", "Pending");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = parseStatus(params.get("status"));
  const lane = parseLane(params.get("lane"));
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  const caseId = params.get("caseId") ?? undefined;
  const page = Math.max(1, Number(params.get("page") ?? "1"));
  const pageSize = Math.min(100, Math.max(1, Number(params.get("pageSize") ?? "25")));
  const sort = params.get("sort") ?? "escalatedAt:desc";

  const [openId, setOpenId] = useState<string | null>(null);
  const [csvCooldownUntil, setCsvCooldownUntil] = useState<number | null>(null);

  const query: ReunderwritingEscalationFilter = useMemo(
    () => ({ status, lane, caseId, from, to, page, pageSize, sort }),
    [status, lane, caseId, from, to, page, pageSize, sort],
  );

  const listQ = useReunderwritingEscalations(flagOn ? query : undefined);
  const detailQ = useReunderwritingEscalation(openId ?? undefined);
  const approveM = useApproveReunderwritingEscalation();
  const denyM = useDenyReunderwritingEscalation();

  const setFilter = useCallback(
    (next: Partial<Record<string, string | null>>) => {
      const merged = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v === null || v === undefined || v === "") merged.delete(k);
        else merged.set(k, v);
      }
      if (Object.keys(next).some((k) => k !== "page")) merged.set("page", "1");
      setParams(merged, { replace: false });
    },
    [params, setParams],
  );

  const handleExportCsv = useCallback(() => {
    const now = Date.now();
    if (csvCooldownUntil && now < csvCooldownUntil) return;
    const url = reunderwritingEscalationsApi.exportCsvUrl(query);
    window.open(url, "_blank");
    setCsvCooldownUntil(now + 60_000);
  }, [csvCooldownUntil, query]);

  useEffect(() => {
    if (!csvCooldownUntil) return;
    const remaining = csvCooldownUntil - Date.now();
    if (remaining <= 0) {
      setCsvCooldownUntil(null);
      return;
    }
    const t = setTimeout(() => setCsvCooldownUntil(null), remaining);
    return () => clearTimeout(t);
  }, [csvCooldownUntil]);

  if (!flagOn) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Reunderwriting disabled</h2>
        <p className="text-small text-gray-mid">
          The Reunderwriting module is not enabled in this environment.
        </p>
      </Card>
    );
  }

  const csvDisabled = !!csvCooldownUntil && Date.now() < csvCooldownUntil;

  return (
    <div className="flex flex-col gap-space-4" data-testid="reunderwriting-escalations">
      <PageHeader
        eyebrow="Reunderwriting"
        title="Escalations"
        description="Analyst escalations from reunderwriting cases awaiting executive review."
        actions={
          <Button
            variant="secondary"
            onClick={handleExportCsv}
            disabled={csvDisabled}
            title={csvDisabled ? "You exported in the last 60 seconds." : "Download filtered escalations as CSV"}
            aria-label="Export filtered reunderwriting escalations as CSV"
          >
            <Download size={16} aria-hidden />
            <span className="ml-space-1">
              {csvDisabled ? "Exported recently" : "Export CSV"}
            </span>
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-4">
        <aside className="lg:col-span-3">
          <ReunderwritingEscalationFilterSidebar
            status={status ?? null}
            lane={lane ?? null}
            from={from ?? ""}
            to={to ?? ""}
            onChange={setFilter}
          />
        </aside>

        <main className="lg:col-span-9">
          <Card className="p-space-4">
            {listQ.isLoading ? (
              <div role="status" aria-live="polite" className="text-gray-mid">
                Loading escalations…
              </div>
            ) : listQ.isError ? (
              <div role="alert" className="flex items-center gap-space-2 text-status-error">
                <ArrowUpCircle size={18} aria-hidden />
                <span>Failed to load escalations.</span>
              </div>
            ) : (
              <ReunderwritingEscalationList
                page={listQ.data ?? { items: [], page: 1, pageSize, totalCount: 0 }}
                sort={sort}
                onSortChange={(s) => setFilter({ sort: s })}
                onPageChange={(p) => setFilter({ page: String(p) })}
                onPageSizeChange={(s) => setFilter({ pageSize: String(s) })}
                onOpen={(id) => setOpenId(id)}
              />
            )}

            {listQ.data?.items.length === 0 && !listQ.isLoading && (
              <div className="mt-space-3 text-gray-mid">
                <p>No escalations match your filters.</p>
                <button
                  type="button"
                  className="text-gold underline mt-space-2"
                  onClick={() =>
                    setFilter({
                      status: null,
                      lane: null,
                      from: null,
                      to: null,
                      page: "1",
                    })
                  }
                >
                  Clear filters
                </button>
              </div>
            )}
          </Card>
        </main>
      </div>

      <ReunderwritingEscalationDetailDrawer
        open={!!openId}
        detail={detailQ.data ?? null}
        isLoading={detailQ.isLoading}
        canResolve={canResolve}
        onClose={() => setOpenId(null)}
        onApprove={(body) =>
          approveM.mutateAsync({ id: openId!, body }).then(() => setOpenId(null))
        }
        onDeny={(body) =>
          denyM.mutateAsync({ id: openId!, body }).then(() => setOpenId(null))
        }
        isSubmitting={approveM.isPending || denyM.isPending}
      />

      <span aria-live="polite" className="sr-only">
        {listQ.data
          ? `${listQ.data.totalCount} escalations match the current filters.`
          : ""}
      </span>

      <span className="sr-only">
        <Badge status="gold" /> Pending · <Badge status="finalized" /> Approved ·{" "}
        <Badge status="error" /> Denied
      </span>
    </div>
  );
}
